import { createBrowserRouter, Navigate, RouterProvider } from "react-router"

import {
  AcceptInvitePage,
  ForgotPasswordPage,
  LoginPage,
  RequireRole,
  ResetPasswordPage,
} from "@/features/auth"
import { IntegrationPage } from "@/features/integration"
import { ClientsPage } from "@/features/clients"
import { EvalLabelPage, EvalRunPage, EvalRunsPage } from "@/features/eval-runs"
import { ProfilePage } from "@/features/profile"
import { QueuePage } from "@/features/queue"
import { ReportOutputPage, ReviewRoute } from "@/features/review"
import { env } from "@/config/env"
import { UsersKeysPage } from "@/features/users-keys"
import { canListClients, canManageUsersAndKeys, canUseDevelopment } from "@/lib/roles"
import { PrimitivesIndexRoute } from "@/routes/dev/primitives"
import { AlertPreviewRoute } from "@/routes/dev/primitives/alert"
import { AvatarPreviewRoute } from "@/routes/dev/primitives/avatar"
import { ButtonPreviewRoute } from "@/routes/dev/primitives/button"
import { ComboboxPreviewRoute } from "@/routes/dev/primitives/combobox"
import { DataTablePreviewRoute } from "@/routes/dev/primitives/data-table"
import { DatePickerPreviewRoute } from "@/routes/dev/primitives/date-picker"
import { DialogPreviewRoute } from "@/routes/dev/primitives/dialog"
import { EmptyStatePreviewRoute } from "@/routes/dev/primitives/empty-state"
import { FormFieldPreviewRoute } from "@/routes/dev/primitives/form-field"
import { IconButtonPreviewRoute } from "@/routes/dev/primitives/icon-button"
import { InputPreviewRoute } from "@/routes/dev/primitives/input"
import { PaginationPreviewRoute } from "@/routes/dev/primitives/pagination"
import { PasswordPreviewRoute } from "@/routes/dev/primitives/password"
import { PopoverPreviewRoute } from "@/routes/dev/primitives/popover"
import { SearchInputPreviewRoute } from "@/routes/dev/primitives/search-input"
import { SelectPreviewRoute } from "@/routes/dev/primitives/select"
import { SheetPreviewRoute } from "@/routes/dev/primitives/sheet"
import { StatCardPreviewRoute } from "@/routes/dev/primitives/stat-card"
import { TableSortPreviewRoute } from "@/routes/dev/primitives/table-sort"
import { TextareaPreviewRoute } from "@/routes/dev/primitives/textarea"
import { ToastPreviewRoute } from "@/routes/dev/primitives/toast"
import { VersionTimelinePreviewRoute } from "@/routes/dev/primitives/version-timeline"
import { NavPlaceholderRoute } from "@/routes/nav-placeholder"

import { ErrorBoundary } from "./error-boundary"
import { NotFoundRoute } from "./not-found-route"
import { ShellRoute } from "./shell-route"

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <ErrorBoundary>
        <LoginPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/accept-invite",
    element: (
      <ErrorBoundary>
        <AcceptInvitePage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <ErrorBoundary>
        <ForgotPasswordPage />
      </ErrorBoundary>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <ErrorBoundary>
        <ResetPasswordPage />
      </ErrorBoundary>
    ),
  },
  {
    element: (
      <ErrorBoundary>
        <ShellRoute />
      </ErrorBoundary>
    ),
    children: [
      /* The queue is the product — what someone opens this application to do.
         `/reviews` stays an alias so links already in circulation hold. */
      { path: "/", element: <QueuePage /> },
      { path: "/reviews", element: <QueuePage /> },
      /* The workspace — a client is redirected to the review's report instead. */
      { path: "/reviews/:id", element: <ReviewRoute /> },
      /* The client report. A static segment, so it ranks above `:version`. */
      { path: "/reviews/:id/output", element: <ReportOutputPage /> },
      { path: "/reviews/:id/:version", element: <ReviewRoute /> },

      /* Your own account: every signed-in role, the client included. */
      { path: "/profile", element: <ProfilePage /> },

      /* Admin pages, each guarded by the predicate its sidebar item uses. GET /clients is
         admin-only server-side, so an unguarded page only rendered a failure. */
      {
        path: "/admin/clients",
        element: (
          <RequireRole allow={canListClients}>
            <ClientsPage />
          </RequireRole>
        ),
      },
      {
        path: "/admin/users",
        element: (
          <RequireRole allow={canManageUsersAndKeys}>
            <UsersKeysPage />
          </RequireRole>
        ),
      },
      ...(env.showDevTools
        ? [
            // The Development group: admins only, every route guarded by the predicate its nav
            // group uses. Behind the dev-tools flag with the nav, so production has neither.
            {
              path: "/evaluations",
              element: (
                <RequireRole allow={canUseDevelopment}>
                  <EvalRunsPage />
                </RequireRole>
              ),
            },
            {
              path: "/evaluations/:runId",
              element: (
                <RequireRole allow={canUseDevelopment}>
                  <EvalRunPage />
                </RequireRole>
              ),
            },
            {
              path: "/evaluations/:runId/labels/:mid",
              element: (
                <RequireRole allow={canUseDevelopment}>
                  <EvalLabelPage />
                </RequireRole>
              ),
            },
            { path: "/admin/rules", element: <NavPlaceholderRoute /> },
            { path: "/tools/label-finder", element: <NavPlaceholderRoute /> },
            { path: "/tools/doc-compare", element: <NavPlaceholderRoute /> },
            { path: "/tools/field-review", element: <NavPlaceholderRoute /> },
            {
              path: "/dev/dataset",
              element: (
                <RequireRole allow={canUseDevelopment}>
                  <NavPlaceholderRoute />
                </RequireRole>
              ),
            },
            {
              path: "/dev/integrations",
              element: (
                <RequireRole allow={canUseDevelopment}>
                  <IntegrationPage />
                </RequireRole>
              ),
            },
            { path: "/dev/openapi", element: <Navigate to="/dev/integrations?tab=docs" replace /> },
          ]
        : []),

      ...(import.meta.env.DEV
        ? [
            { path: "/dev/primitives", element: <PrimitivesIndexRoute /> },
            { path: "/dev/primitives/alert", element: <AlertPreviewRoute /> },
            { path: "/dev/primitives/avatar", element: <AvatarPreviewRoute /> },
            { path: "/dev/primitives/button", element: <ButtonPreviewRoute /> },
            { path: "/dev/primitives/combobox", element: <ComboboxPreviewRoute /> },
            { path: "/dev/primitives/data-table", element: <DataTablePreviewRoute /> },
            { path: "/dev/primitives/date-picker", element: <DatePickerPreviewRoute /> },
            { path: "/dev/primitives/dialog", element: <DialogPreviewRoute /> },
            { path: "/dev/primitives/empty-state", element: <EmptyStatePreviewRoute /> },
            { path: "/dev/primitives/form-field", element: <FormFieldPreviewRoute /> },
            { path: "/dev/primitives/icon-button", element: <IconButtonPreviewRoute /> },
            { path: "/dev/primitives/input", element: <InputPreviewRoute /> },
            { path: "/dev/primitives/pagination", element: <PaginationPreviewRoute /> },
            { path: "/dev/primitives/password", element: <PasswordPreviewRoute /> },
            { path: "/dev/primitives/popover", element: <PopoverPreviewRoute /> },
            { path: "/dev/primitives/search-input", element: <SearchInputPreviewRoute /> },
            { path: "/dev/primitives/select", element: <SelectPreviewRoute /> },
            { path: "/dev/primitives/sheet", element: <SheetPreviewRoute /> },
            { path: "/dev/primitives/stat-card", element: <StatCardPreviewRoute /> },
            { path: "/dev/primitives/table-sort", element: <TableSortPreviewRoute /> },
            { path: "/dev/primitives/textarea", element: <TextareaPreviewRoute /> },
            { path: "/dev/primitives/toast", element: <ToastPreviewRoute /> },
            { path: "/dev/primitives/version-timeline", element: <VersionTimelinePreviewRoute /> },
          ]
        : []),
    ],
  },
  /* Anything no route above claims. Beside the shell, not inside it, so it is decided before
     the sign-in check; NotFoundRoute then puts the shell around it for a signed-in reader.
     Without it React Router renders its developer error. */
  {
    path: "*",
    element: (
      <ErrorBoundary>
        <NotFoundRoute />
      </ErrorBoundary>
    ),
  },
])

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />
}
